/**
 * ThumbWithFallback — defensive image that survives transient ingress /
 * Cloudflare 4xx-5xx hiccups (e.g. the Restaurant spoon incident where
 * a file that lives on disk momentarily served 502 via the preview URL).
 *
 * Behaviour:
 *   1. First load attempt uses the given src as-is.
 *   2. On error, wait 800 ms and retry once with a cache-bust query
 *      (?_r={now}) — a fresh request skips any bad edge cache entry.
 *   3. If the second attempt also fails, render a small placeholder tile
 *      with a ↻ button so the admin can manually reload without opening
 *      the whole editor page again.
 *   4. When the `src` prop changes, reset the retry state so the new
 *      URL starts clean.
 *
 * This does NOT attempt to fix genuinely missing files — for that see
 * the sweep run in the backend. But it keeps a two-second network blip
 * from surfacing as a permanent broken-image icon.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const RETRY_DELAY_MS = 800;

export function ThumbWithFallback({
  src,
  alt = "",
  className,
  style,
  "data-testid": testId,
  placeholderLabel = "image unavailable",
}) {
  const [attempt, setAttempt] = useState(0); // 0 = first, 1 = cache-busted retry
  const [failed, setFailed] = useState(false);
  const timerRef = useRef(null);

  // Reset retry state whenever the source URL changes.
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [src]);

  const onError = useCallback(() => {
    if (attempt === 0) {
      // First failure — schedule one cache-busted retry.
      timerRef.current = setTimeout(() => setAttempt(1), RETRY_DELAY_MS);
    } else {
      setFailed(true);
    }
  }, [attempt]);

  const onReload = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFailed(false);
    // Force a full round-trip by incrementing attempt (used in cache-bust query).
    setAttempt((n) => n + 1);
  }, []);

  if (!src) return null;

  if (failed) {
    return (
      <div
        className={className}
        data-testid={testId ? `${testId}-fallback` : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "0.25rem",
          background: "#fff4e0",
          border: "1px dashed var(--wmleye-line, #c8b8a4)",
          borderRadius: 8,
          color: "#8a6403",
          fontSize: "0.7rem",
          padding: "0.3rem",
          textAlign: "center",
          ...style,
        }}
      >
        <span>{placeholderLabel}</span>
        <button
          type="button"
          onClick={onReload}
          data-testid={testId ? `${testId}-reload` : undefined}
          style={{
            fontSize: "0.65rem",
            padding: "0.15rem 0.4rem",
            borderRadius: 8,
            border: "1px solid #c8b8a4",
            background: "#fff",
            color: "#8a6403",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          ↻ Reload
        </button>
      </div>
    );
  }

  // Cache-bust on retry attempts. Preserve any existing ?v= query.
  const bust = attempt > 0 ? `${src.includes("?") ? "&" : "?"}_r=${attempt}${Date.now()}` : "";

  return (
    <img
      src={src + bust}
      alt={alt}
      className={className}
      style={style}
      data-testid={testId}
      onError={onError}
      onLoad={() => setFailed(false)}
    />
  );
}
